'use strict';

import polyline from '@mapbox/polyline';
import toGeoJSON from '@mapbox/togeojson';

const turf_bbox = require( '@turf/bbox' );
const turf_bezierSpline = require( '@turf/bezier-spline' );
const turf_getCoords = require( '@turf/invariant' ).getCoords;
const turf_lineString = require( '@turf/helpers' ).lineString;
// const turf_nearestPointOnLine = require( '@turf/nearest-point-on-line' );
const turf_nearestPointOnLine = require( './vendor/turf-nearest-point-on-line' );
const turf_polygon = require( '@turf/helpers' ).polygon;
const turf_point = require( '@turf/helpers' ).point;
const turf_simplify = require( '@turf/simplify' );

const rectangleAroundLine = require( './utils' ).rectangleAroundLine;

import MapUIInstance from './map-ui-instance';

const invert = ( object ) => Object.entries( object ).reduce( ( acc, [ key, value ] ) => ( { ...acc, [ value ]: key, } ), {} );

export default class MapUIMapboxInstance extends MapUIInstance {

  constructor( container ) {

    super( container );

    this.instance = null;
    this.instance_options = null;

    this.polyline = polyline;

    mapboxgl.accessToken = 'pk.eyJ1IjoiaWFtYnoiLCJhIjoiY2lscmthdDRoMDhwb3RsbTEyZGluaWpxeCJ9.gmPjGJEZNBmhkpKLAZU3HA';

    setTimeout( () => { this.init(); }, 0 );

  }

  init() {

    _wp.c.d && console.debug( 'MapUIMapboxInstance::init', this.options );

    this.instance_options = {

      container: this.options.dom_id,
      center: ( false !== this.options.center ) ? [ this.options.center[ 1 ], this.options.center[ 0 ] ] : [ 0, 0 ],
      bearing: ( false !== this.options.bearing ) ? this.options.bearing : 0,
      zoom: ( false !== this.options.zoom ) ? this.options.zoom : 1,
      style: ( 'string' === typeof this.options.map_style && map_ui._styles.hasOwnProperty( this.options.map_style ) ) ? map_ui._styles[ this.options.map_style ] : map_ui._styles.default,

      attributionControl: false,

      minZoom: 3

    };

    // &nbsp; breaks the whole thing!
    // So let's empty the container before starting
    // const dom = document.getElementById( this.options.dom_id );
    while ( this.dom_container.firstChild ) {
      this.dom_container.removeChild( this.dom_container.firstChild );
    }

    this.dom_container.parentNode.classList.add( 'mapbox' );

    if ( false !== this.options.zoom_range ) {

      this.instance_options.minZoom = this.options.zoom_range[ 0 ];
      this.instance_options.maxZoom = this.options.zoom_range[ 1 ];

    }

    if ( this.options.locations.length ) {
      this.options.locations = this._locations_to_geojson( this.options.locations );
    }

    if ( false === this.options.center ) {
      this._re_center();
    }

    this.legend_container = document.createElement( 'div' );
    this.legend_container.classList.add( 'legend-container' );
    document.getElementById( this.options.dom_id ).parentNode.insertBefore( this.legend_container, document.getElementById( this.options.dom_id ).nextSibling );

    if ( undefined !== this.options.title && this.options.title ) {
      this.init_title();
    }

    try {

      this.instance = new mapboxgl.Map( this.instance_options );
      this.instance.on( 'webglcontextlost ', () => { _wp.c.d && console.error( 'webgl context lost' ); } );

      this.instance.on( 'style.load', () => {

        this.fire( 'style.load' );
        map_ui.fire( 'style.load', { map: this } );
        this.on_load();

      } );
      this.instance.on( 'load', () => {

        this.fire( 'map.load' );
        map_ui.fire( 'map.load', { map: this } );

      } );

      this.instance.scrollZoom.disable();
      this.instance.addControl( new mapboxgl.NavigationControl(), 'bottom-right' );

    }
    catch ( e ) {
      console.error( e );
      _wp.c.d && console.error( this.instance_options );
    }

    this.fire( 'map.init' );
    map_ui.fire( 'map.init', { map: this } );

  }

  async on_load() {

    if ( _wp.c.d ) {
      this.init_debug.bind( this ).call();
    }

    const layers = [ 'city', 'poi-lg', 'poi', 'city-sm',
      'country-label', 'state-label', 'poi-label',
      'water-point-label', 'water-line-label', 'natural-point-label', 'natural-line-label', 'waterway-label' ];

    layers.forEach( ( layer ) => {

      this.instance.setLayoutProperty( layer, 'text-field', [ "coalesce", ["get", "name_${_wp.l}" ], [ "get", "name" ] ] );
      this.instance.setLayerZoomRange( layer, 0, 24 );

      let filter = this.instance.getFilter( layer );

      if ( 'all' !== filter[ 0 ] ) {
        filter = [ 'all', [ ...filter ] ];
      }
      filter.push( [ "all", [ ">=", [ "zoom" ], [ "get", "min_zoom" ] ], [ "<=", ["zoom"], [ "get", "max_zoom" ] ] ] );

      this.instance.setFilter( layer, filter );

    } );

    this.instance.addSource( 'markers', {
      'type': 'geojson',
      'data': { "type": "FeatureCollection", "features": [] },
    } );

    if ( this.options.locations && this.options.locations.features && this.options.locations.features.length ) {
      this.init_labels.bind( this ).call();
    }

    const castArray = ( ...args ) => ( args[ 0 ] instanceof Array ? args[ 0 ] : args );

    if ( undefined !== this.options.path ) {
      castArray( this.options.path ).forEach( async ( path_data ) => {

        if ( undefined !== path_data.data ) {
          const data = await map_ui._load_data( path_data.data );
          this.init_path( path_data, data );
        }
        else if ( undefined !== path_data.polyline ) {
          this.init_path( path_data, path_data.polyline );
        }
        else if ( undefined !== path_data.polygon ) {
          this.init_polygon( path_data, path_data.polygon );
        }

      } );
    }

    if ( undefined !== this.options.elevation && undefined !== this.options.elevation.data ) {
      const elevation_data = await map_ui._load_data( this.options.elevation.data );
      this.init_elevation_chart( elevation_data );
    }

    // Debug
    // this.instance.showCollisionBoxes = true;

  }

  init_title() {

    const legend = document.createElement( 'div' );
    legend.classList.add( 'legend' );
    legend.id = 'legend';
    legend.innerHTML = `<h4>${( 'object' === typeof this.options.title ? this.options.title[ _wp.l ] : this.options.title )}</h4>`;
    this.legend_container.appendChild( legend );

  }

  _re_center() {

    let llb = null;
    let in_bounds_locations;

    // We have an instance already, probably with data
    if ( null !== this.instance ) {
      if ( undefined !== this.instance.getSource( '_path-elevation' ) ) {
        llb = turf_bbox( this.instance.getSource( '_path-elevation' )._data );
      }
      else if ( undefined !== this.instance.getSource( 'markers' ) ) {
        in_bounds_locations = this._extract_geojson_features_by_property( this.instance.getSource( 'markers' )._data, 'in_bounds', true );
      }
    }
    else {
      in_bounds_locations = this._extract_geojson_features_by_property( this.options.locations, 'in_bounds', true );
    }

    _wp.c.d && console.debug( 'in_bounds_locations', in_bounds_locations );

    if ( undefined === llb ) {

      if ( in_bounds_locations.features.length > 1 ) {

        llb = new mapboxgl.LngLatBounds(
          new mapboxgl.LngLat( in_bounds_locations.features[ 0 ].geometry.coordinates[ 0 ], in_bounds_locations.features[ 0 ].geometry.coordinates[ 1 ] ),
          new mapboxgl.LngLat( in_bounds_locations.features[ 0 ].geometry.coordinates[ 0 ], in_bounds_locations.features[ 0 ].geometry.coordinates[ 1 ] )
        );

        in_bounds_locations.features.forEach( ( feature ) => {
          llb = llb.extend( new mapboxgl.LngLat( feature.geometry.coordinates[ 0 ], feature.geometry.coordinates[ 1 ] ) );
        } );

        this.instance_options.center = llb.getCenter();

      }
      else if ( 1 === this.options.locations.features.length ) {

        this.options.center = this.options.locations.features[ 0 ].geometry.coordinates;
        this.instance_options.center = this.options.center;

      }

    }

    if ( null !== this.instance && null !== llb ) {

      this.instance.fitBounds( llb, { linear: true, padding: 40, easing: ( t ) => { return t; }, } );
      this.instance.once( 'moveend', () => {

        this.instance.setZoom( Math.floor( this.instance.getZoom() ) );

        _wp.c.d && console.debug( 're-center done.' );
        _wp.c.d && console.debug( `Center: ${this.instance.getCenter().lat.toFixed( 5 )}, ${this.instance.getCenter().lng.toFixed( 5 )}` );
        _wp.c.d && console.debug( `Zoom: ${this.instance.getZoom()}` );

      } );

    }

    return this.instance_options.center;

  }

  init_layers_for_labels( locations ) {

    locations.features.forEach( ( location ) => {

      if ( this.instance.getLayer( location.properties.type ) ) {

        require( 'mapbox-gl-utils' ).init( this.instance );
        this.instance.U.setLayerSource( location.properties.type, 'markers', null );
        this.instance.U.show( location.properties.type );

        console.log( `show layer ${location.properties.type}` );

      }
      else {
        _wp.c.d && console.error( `No declaration available for ${location.properties.type}` );
      }

    } );

  }

  init_labels() {

    const d = this.instance.getSource( 'markers' )._data;
    d.features = d.features.concat( this.options.locations.features );
    this.instance.getSource( 'markers' ).setData( d );

    this.init_layers_for_labels( this.options.locations );

  }

  init_path( options, data ) {

    if ( undefined !== options.data ) {

      // This assumes the loaded file is KML
      // Maybe add options for the future
      data = ( new DOMParser() ).parseFromString( data, 'text/xml' );
      data = toGeoJSON.kml( data );

    }
    else if ( undefined !== options.polyline && ( 'string' === typeof data ) ) {

      data = {
        features: [
          {
            'geometry': polyline.toGeoJSON( data ),
            'type': "Feature",
          }
        ],
        type: "FeatureCollection"
      };

    }
    else if ( undefined !== options.polyline && Array.isArray( data ) ) {

      data = {
        features: [
          {
            'geometry': {
              type: "LineString",
              coordinates: data,
            },
            'type': "Feature",
          }
        ],
        type: "FeatureCollection",
      };

    }

    if ( undefined === options.id ) {
      options.id = _f.uuid();
    }

    // Extract markers from path to add to markers

    this.instance.addSource( `_path-${options.id}`, {
      'type': 'geojson',
      'data': this._extract_geojson_features_by_geotype( data, 'LineString' )
    } );

    this.draw_path( options.id, `_path-${options.id}`, options, '_path-placeholder' );

    if ( options.show_data_markers ) {

      const d = this.instance.getSource( 'markers' )._data;
      const features = this._locations_to_geojson( this._extract_geojson_features_by_geotype( data, 'Point' ).features ).features;

      if ( options.color_icons ) {
        const colors = invert( window.map_ui.color );
        features.forEach( ( feature, i ) => {
          if ( ( Array.isArray( options.color_icons ) && options.color_icons.includes( feature.properties.type ) ) || ( 'string' === typeof options.color_icons && 'all' === options.color_icons ) ) {
            feature.properties.icon_variant = `${colors[ options.color ]}`;
          }
        } );
      }

      d.features = d.features.concat( features );

      this.init_layers_for_labels( d );
      this.instance.getSource( 'markers' ).setData( d );

    }

  }

  draw_path( name, source, options, position ) {

    this.instance.addLayer( {
      'id': `_path-${name}`,
      'type': 'line',
      'source': source,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round',
      },
      'paint': {
        'line-color': options.color,
        'line-width': options.width,
        'line-opacity': {
          'stops': [ [ ( options.min_zoom - 1 ), 0 ], [ options.min_zoom, 1 ] ],
        },
      },
    }, position );

    this.instance.addLayer( {
      'id': `_path-${name}-outline`,
      'type': 'line',
      'source': source,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round',
      },
      'paint': {
        'line-color': 'hsla(0, 0%, 0%, 0.14)',
        'line-width': options.width,
        'line-opacity': {
          'stops': [ [ ( options.min_zoom - 1 ), 0 ], [ options.min_zoom, 0.8 ] ],
        },
        'line-translate': [ 1, 1 ],
      },
    }, `_path-${name}` );

    this.instance.addLayer( {
      'id': `_path-${name}-outline2`,
      'type': 'line',
      'source': source,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round',
      },
      'paint': {
        'line-color': 'hsla(0, 0%, 0%, 0.14)',
        'line-width': options.width,
        'line-blur': 1,
        'line-opacity': {
          'stops': [ [ ( options.min_zoom - 1 ), 0 ], [ options.min_zoom, 0.8 ] ],
        },
        'line-translate': [ 1, 1 ],
      },
    }, `_path-${name}-outline` );

  }

  init_polygon( options, data ) {

    if ( undefined !== options.data ) {

      // This assumes the loaded file is KML
      // Maybe add options for the future
      data = ( new DOMParser() ).parseFromString( data, 'text/xml' );
      data = toGeoJSON.kml( data );

    }
    else if ( undefined !== options.polygon ) {

      data = Array.isArray( data ) ? data : [ data ]
      data = data.map( ( d ) => {
        if ( ( 'string' === typeof d ) ) {
          d = polyline.toGeoJSON( d ).coordinates;
          d.push( d[ 0 ] );
          return d;
        }
        return d;
      } );

      data = {
        features: [
          {
            'geometry': {
              type: "Polygon",
              coordinates: data,
            },
            'type': "Feature",
          }
        ],
        type: "FeatureCollection",
      };

    }

    if ( undefined === options.id ) {
      options.id = _f.uuid();
    }

    for ( const i in data.features ) {
      for ( const j in data.features[ i ].geometry.coordinates ) {
        const originalLength = data.features[ i ].geometry.coordinates[ j ].length;
        data.features[ i ].geometry.coordinates[ j ] = turf_getCoords( turf_simplify( turf_polygon( [ data.features[ i ].geometry.coordinates[ j ] ] ), { tolerance: 0.0001, highQuality: true } ) )[ 0 ];
        data.features[ i ].geometry.coordinates[ j ] = turf_getCoords( turf_bezierSpline.default( turf_lineString( data.features[ i ].geometry.coordinates[ j ] ), { resolution: originalLength * ( 100000 / 2500 ), sharpness: 0.85 } ) );
      }
    }

    this.instance.addSource( `_path-${options.id}`, {
      'type': 'geojson',
      'data': this._extract_geojson_features_by_geotype( data, 'Polygon' )
    } );

    this.draw_polygon( options.id, `_path-${options.id}`, options, '_polygon-placeholder' );

  }

  draw_polygon( name, source, options, position ) {

    this.instance.addLayer( {
      'id': `_path-${name}`,
      'type': 'fill',
      'source': source,
      'layout': {},
      'paint': {
        'fill-antialias': true,
        'fill-outline-color': options.color,
        'fill-color': options.color,
        'fill-opacity': {
          'stops': [ [ ( options.min_zoom - 1 ), 0 ], [ options.min_zoom, options.opacity ] ],
        },
      },
    }, position );

    // this.instance.addLayer( {
    //   'id': `_path-${name}-outline`,
    //   'type': 'line',
    //   'source': source,
    //   'layout': {
    //     'line-join': 'round',
    //     'line-cap': 'round',
    //   },
    //   'paint': {
    //     'line-color': 'hsla(0, 0%, 0%, 0.14)',
    //     'line-width': options.width,
    //     'line-opacity': {
    //       'stops': [ [ ( options.min_zoom - 1 ), 0 ], [ options.min_zoom, 0.8 ] ],
    //     },
    //     'line-translate': [ 1, 1 ],
    //   },
    // }, `_path-${name}` );

    // this.instance.addLayer( {
    //   'id': `_path-${name}-outline2`,
    //   'type': 'line',
    //   'source': source,
    //   'layout': {
    //     'line-join': 'round',
    //     'line-cap': 'round',
    //   },
    //   'paint': {
    //     'line-color': 'hsla(0, 0%, 0%, 0.14)',
    //     'line-width': options.width,
    //     'line-blur': 1,
    //     'line-opacity': {
    //       'stops': [ [ ( options.min_zoom - 1 ), 0 ], [ options.min_zoom, 0.8 ] ],
    //     },
    //     'line-translate': [ 1, 1 ],
    //   },
    // }, `_path-${name}-outline` );

  }

  init_elevation_chart( data ) {

    data = this.prepare_elevation_data( data );
    this.elevation_data_ready( data );

  }

  async elevation_data_ready( data ) {

    await this.container._load_engine( 'd3' );
    this.build_elevation_chart( data );

  }

  _get_chart_container() {

    if ( undefined === this._chart_container ) {

      let dom = document.createElement( 'div' );
      dom.classList.add( 'chart-container' );
      this._chart_container = document.getElementById( this.options.dom_id ).parentNode.insertBefore( dom, document.getElementById( this.options.dom_id ).nextSibling );

      dom = document.createElement( 'div' );
      dom.classList.add( 'chart-container-mask' );
      this._chart_container.parentNode.insertBefore( dom, this._chart_container );

    }

    return this._chart_container;

  }

  build_elevation_chart( data ) {

    d3.selection.prototype.position = function() {

      const el = this.node().getBoundingClientRect();

      return {
        top: el.top,
        right: el.right,
        bottom: el.bottom,
        left: el.left,
        width: el.width,
        height: el.height,
        x: el.x,
        y: el.y
      };

    };

    this.options.elevation._data = data;

    if ( this.options.elevation.view_3d ) {

      this.options.elevation.view_3d = _f.extend( {}, {
        duration: 2000,
        extrusion_factor: 1,
        extrusion_block_width: 10,
      }, this.options.elevation.view_3d );

    }

    // Once, we may need to switch the camera view
    if ( this.options.elevation.view_3d && this.options.elevation.view_3d.hasOwnProperty( 'center' ) ) {

      const tmp = Object.assign( {}, this.options.elevation.view_3d.center );
      this.options.elevation.view_3d.center[ 0 ] = tmp[ 1 ];
      this.options.elevation.view_3d.center[ 1 ] = tmp[ 0 ];

    }

    const source = this._extract_geojson_features_by_geotype( data.geojson, 'LineString' );

    this.instance.addSource( '_path-elevation', {
      'type': 'geojson',
      'data': source,
    } );

    if ( this.options.elevation.view_3d ) {

      this.options.elevation._data.extrusion = {
        'type': 'FeatureCollection',
        'features': [],
      };

      // https://en.wikipedia.org/wiki/Decimal_degrees
      // 0.00001 - equivalent to precision of 1.1132 m
      const rect_width = this.options.elevation.view_3d.extrusion_block_width * 0.00001;

      source.features.forEach( ( feature, i ) => {

        let prev = false;

        source.features[ i ].geometry.coordinates.forEach( ( coordinate, j ) => {

          if ( false !== prev ) {

            // https://stackoverflow.com/a/7854359
            const polygon = rectangleAroundLine( prev, coordinate, rect_width );

            if ( false !== polygon ) {

              this.options.elevation._data.extrusion.features = this.options.elevation._data.extrusion.features.concat( _f.extend( {},
                turf_polygon( [ [ ...polygon, polygon[ 0 ] ] ] ),
                {
                  'properties': {
                    'elev': ( coordinate[ 2 ] - this.altitude_to_global_unit( data.elevation_stats.min[ 2 ] ) ) * this.options.elevation.view_3d.extrusion_factor,
                    'point': coordinate,
                  }
                } ) );

            }

          }

          prev = coordinate;

        } );

      } );

      this.instance.setLight( { intensity: 0.4, position: [ 1, 180, 60 ] } );

      this.instance.addSource( '_path-elevation-extrusion', {
        'type': 'geojson',
        'data': this.options.elevation._data.extrusion,
      } );

    }

    if ( this.options.elevation.show_data_markers ) {

      const d = this.instance.getSource( 'markers' )._data;
      const features = this._locations_to_geojson( this._extract_geojson_features_by_geotype( data.geojson, 'Point' ).features ).features;

      if ( this.options.elevation.color_icons ) {
        const colors = invert( window.map_ui.color );
        features.forEach( ( feature, i ) => {
          if ( ( Array.isArray( this.options.elevation.color_icons ) && this.options.elevation.color_icons.includes( feature.properties.type ) ) || ( 'string' === typeof this.options.elevation.color_icons && 'all' === this.options.elevation.color_icons ) ) {
            feature.properties.icon_variant = `${colors[ this.options.elevation.color ]}`;
          }
        } );
      }

      d.features = d.features.concat( features );

      this.init_layers_for_labels( d );
      this.instance.getSource( 'markers' ).setData( d );

    }

    if ( this.options.elevation.show_path_tooltip ) {

      this.instance.addLayer( {
        'id': '_path-elevation-target',
        'type': 'line',
        'source': '_path-elevation',
        'layout': {
          'line-join': 'round',
          'line-cap': 'round',
        },
        'paint': {
          'line-width': 30,
          'line-opacity': 0,
        },
      }, '_path-placeholder' );

    }

    this.draw_path( 'elevation', '_path-elevation', this.options.elevation, ( this.options.elevation.show_path_tooltip ? '_path-elevation-target' : '_path-placeholder' ) );

    if ( this.options.elevation.view_3d ) {

      this.instance.addLayer( {
        'id': '_path-elevation-extrusion',
        'type': 'fill-extrusion',
        'source': '_path-elevation-extrusion',
        'layout': {
          'visibility': 'none',
        },
        'paint': {
          'fill-extrusion-height': {
            'property': 'elev',
            'type': 'identity'
          },
          'fill-extrusion-color': this.options.elevation.color,
          'fill-extrusion-opacity': 0.64,
        },
      } );

    }

    if ( this.options.elevation.show_path_tooltip ) {

      const popup =  new mapboxgl.Popup( {
        closeButton: false,
        closeOnClick: false,
        offset: {
          'bottom': [ 0, -8 ],
        },
      } );

      // Add marker data as a new GeoJSON source.
      this.instance.addSource( '_elevation_marker', {
        'type': 'geojson',
        'data':  {
          "type": "Point",
          "coordinates": [ 0, 0 ]
        }
      } );

      // Add a layer showing the markers.
      this.instance.addLayer( {
        'id': '_elevation_marker',
        'type': 'symbol',
        'source': '_elevation_marker',
        'layout': {
          'icon-image': 'city',
          'icon-allow-overlap': true
        }
      } );

      this.currentAltitudePathLocation = false;

      this.instance.on( 'click', () => {
        if ( this.currentAltitudePathLocation ) {
          _wp.c.d && console.log( this.currentAltitudePathLocation );
        }
      } );

      const elevation_line_string = turf_lineString( data.elevation_data_simplified );

      this.instance.on( 'mousemove', ( e ) => {

        const features = this.instance.queryRenderedFeatures( e.point, { layers: [ '_path-elevation-target' ] } );
        this.instance.getCanvas().style.cursor = ( features.length ) ? 'pointer' : '';

        if ( features.length ) {
          this.currentAltitudePathLocation = turf_nearestPointOnLine.default(
            elevation_line_string,
            turf_point( [ e.lngLat.lng, e.lngLat.lat, 0, 0 ] )
          );

          if ( this.currentAltitudePathLocation && this.currentAltitudePathLocation.geometry.coordinates[ 2 ] ) {

            this.instance.setLayoutProperty( '_elevation_marker', 'visibility', 'visible'  );
            this.instance.getSource( '_elevation_marker' ).setData( {
              'type': 'Point',
              'coordinates': [ this.currentAltitudePathLocation.geometry.coordinates[ 0 ], this.currentAltitudePathLocation.geometry.coordinates[ 1 ] ]
            } );

            popup.setLngLat( mapboxgl.LngLat.convert( this.currentAltitudePathLocation.geometry.coordinates.slice( 0, 2 ) ) )
              .setText( this.options.elevation.tooltip_html[ _wp.l ]
                .replace( '%distance%', this.format_distance( this.currentAltitudePathLocation.geometry.coordinates[ 3 ] ) )
                .replace( '%altitude%', this.format_altitude( this.currentAltitudePathLocation.geometry.coordinates[ 2 ] ) )
              )
              .addTo( this.instance );

          }
        }
        else {
          this.currentAltitudePathLocation = false;
        }

        if ( ! this.currentAltitudePathLocation ) {

          popup.remove();
          this.instance.setLayoutProperty( '_elevation_marker', 'visibility', 'none'  );

        }

      } );

    }

    //

    let dom;

    if ( this.options.elevation.show_chart ) {

      dom = this._get_chart_container();

      const chart_container = document.createElement( 'div' );
      chart_container.classList.add( 'elevation-chart-container' );
      dom.appendChild( chart_container );

      const chart = document.createElement( 'div' );
      chart.classList.add( 'elevation-chart' );
      chart_container.appendChild( chart );

      const bleed = { top: 20, right: 20, bottom: 20, left: 20 };
      const width = Math.round( chart.getBoundingClientRect().width );
      const height = Math.round( chart.getBoundingClientRect().height );

      const svg = d3.select( chart )
        .append( 'svg' )
        .attr( 'width', width + bleed.left + bleed.right )
        .attr( 'height', height + bleed.top + bleed.bottom )
        .attr( 'style', `margin-left: -${bleed.left}px; margin-top: -${bleed.top}px;` );

      const defs = svg.append( 'defs' );

      const _chart_data = data.elevation_data_simplified;
      const x = d3.scaleLinear().range( [ 0, width ] );
      const y = d3.scaleLinear().range( [ height - 8, 8 ] );

      const area = d3.line()
        .x( ( d ) => { return x( d[ 3 ] ); } ) // Distance
        .y( ( d ) => { return y( d[ 2 ] ); } ); // Altitude

      x.domain( [ _chart_data[ 0 ][ 3 ], _chart_data[ _chart_data.length - 1 ][ 3 ] ] );
      y.domain( d3.extent( _chart_data, ( d ) => { return d[ 2 ]; } ) );

      const yAxis = d3.axisRight( y )
        .tickSize( -width )
        .ticks( 3 )
        .tickFormat( ( d ) => { return this.format_altitude( d, true, false ); } );

      svg.append( "g" )
        .attr( "class", "y axis" )
        .attr( 'transform', `translate(${( bleed.left + width )},${bleed.top})` )
        .call( yAxis )
        .selectAll( "text" )
        .attr( "style", 'text-anchor:end' )
        .attr( "x", '-4' )
        .attr( "y", '-8' );

      _f.write_style( `.${this.options.dom_id} .axis.y { fill: transparent; }` );
      _f.write_style( `.${this.options.dom_id} .tick line { stroke: ${this.options.elevation.color}; stroke-opacity: .08; stroke-width: 1; shape-rendering: crispEdges; }` );
      _f.write_style( `.${this.options.dom_id} .tick text { font-size: .6rem; fill: ${this.options.elevation.color}; text-anchor: end; fill-opacity: .4; }` );

      _f.write_style( `.${this.options.dom_id} .area { stroke: url(#${this.options.dom_id}-elevation-gradient-fill); stroke-width: 2; fill: transparent; }` );
      _f.write_style( `.${this.options.dom_id} .focus circle { fill: url(#${this.options.dom_id}-elevation-gradient-fill); }` );

      defs.append( 'clipPath' )
        .attr( 'id', 'elevation-rounded-clip' )
        .append('rect')
        .attr( 'rx', 4 )
        .attr( 'ry', 4 )
        .attr( 'width', width )
        .attr( 'height', height );

      svg.append( 'linearGradient' )
        .attr( 'rx', 6 )
        .attr( 'ry', 6 )
        .attr( 'id', `${this.options.dom_id}-elevation-gradient-fill` )
        .attr( 'gradientUnits', 'userSpaceOnUse' )
        .attr( 'transform', `translate(${bleed.left},${bleed.top})` )
        .attr( 'x1', 0 ).attr( 'y1', y( this.format_altitude( data.elevation_stats.min[ 2 ], false ) ) )
        .attr( 'x2', 0 ).attr( 'y2', y( this.format_altitude( data.elevation_stats.max[ 2 ], false ) ) )
        .selectAll( 'stop' )
        .data( [
          { offset: '0%', color: _f.rgba( this.options.elevation.color, 0.5 ) },
          { offset: '100%', color: _f.rgba( this.options.elevation.color, 0.8 ) }
        ] )
        .enter().append( 'stop' )
        .attr( 'offset', ( d ) => { return d.offset; } )
        .attr( 'stop-color', ( d ) => { return d.color; } );

      svg.append( 'path' )
        .datum( _chart_data )
        .attr( 'class', 'area' )
        .attr( 'clip-path', () => { return 'url(#elevation-rounded-clip)'; } )
        .attr( 'transform', `translate(${bleed.left},${bleed.top})` )
        .attr( 'd', area );

      const focus = svg.append( 'g' )
        .attr( 'class', 'focus' )
        .style( 'display', 'none' );

      focus.append( 'circle' )
        .attr( 'r', 5 );

      const tooltip = new window._tooltipsHandler( `elevation-tooltip-${this.options.dom_id}`, 'elevation-tooltip small', { background: this.options.elevation.color, color: '#ffffff' } );
      tooltip.addBeacon();
      const bisectDist = d3.bisector( ( d ) => { return d[ 3 ]; } ).left;

      const rect = svg.append( 'rect' )
        .attr( 'class', 'overlay' )
        .attr( 'width', width )
        .attr( 'height', height )
        .attr( 'transform', `translate(${bleed.left},${bleed.top})` );

      //

      const isMobile = window.innerWidth < 640 ? true : false;
      const offset = isMobile ? [ 0, 50 ] : [ 0, 20 ];

      const alsoUpdateExtrusion = this.options.elevation.view_3d ? ( d ) => {

        this.instance.getSource( '_path-elevation-extrusion' ).setData( {
          'type': 'FeatureCollection',
          'features': this.options.elevation._data.extrusion.features.filter( ( value ) => { return value.properties.point[ 3 ] <= d[ 3 ]; } )
        } );

        const featureNow = this.instance.getSource( '_path-elevation-extrusion' ).serialize().data.features.pop();

        if ( undefined !== featureNow ) {

          this.instance.easeTo( {
            center: [ featureNow.properties.point[ 0 ], featureNow.properties.point[ 1 ] ],
            duration: 500,
            offset: offset,
            easing: ( t ) => { return ( t < .5 ) ? ( 2 * t * t ) : ( -1 + ( 4 - 2 * t ) * t ); }
          } );

        }

      } : () => {};
      const alsoResetExtrusion = this.options.elevation.view_3d ? () => {

        this.instance.getSource( '_path-elevation-extrusion' ).setData( {
          'type': 'FeatureCollection',
          'features': this.options.elevation._data.extrusion.features,
        } );

      } : () => {};

      rect.on( 'mouseover', () => {
          focus.style( 'display', null );
        } )
        .on( 'mouseout', () => {

          focus.style( 'display', 'none' );
          tooltip._hide();
          alsoResetExtrusion();

        } )
        .on( 'mousemove', () => {

          const x0 = x.invert( d3.mouse( rect.node() )[ 0 ] );
          const i = Math.min( bisectDist( _chart_data, x0, 1 ), _chart_data.length - 1 );

          const d0 = _chart_data[ i - 1 ];
          const d1 = _chart_data[ i ];
          const d = ( x0 - d0[ 2 ] > d1[ 2 ] - x0 ) ? d1 : d0;

          focus.attr( 'transform', `translate(${( x( d[ 3 ] ) + bleed.left )},${( y( d[ 2 ] ) + bleed.top )})` );

          const pos = focus.position();
          pos.top = _f.getScrollPosition() + pos.top + ( pos.height / 2 );
          pos.left = pos.left + ( pos.width / 2 );
          tooltip._showOnBeacon( pos, tooltip, this.options.elevation.tooltip_html[ _wp.l ].replace( "%distance%", this.format_distance( d[ 3 ] ) ).replace( "%altitude%", this.format_altitude( d[ 2 ] ) ) );

          alsoUpdateExtrusion( d );

        } );

    }

    if ( this.options.elevation.show_stats || this.options.elevation.show_chart ) {

      dom = this._get_chart_container();

      // Add button
      const btn = document.createElement( 'div' );
      btn.classList.add( 'btn' );
      btn.classList.add( 'info-chart' );
      btn.id = 'btn';
      btn.innerHTML = `<h5>${this.options.elevation.section_title[ _wp.l ]}</h5>`;
      btn.setAttribute( 'data-tooltip', this.options.elevation.section_title[ _wp.l ] );
      this.legend_container.appendChild( btn );

      const mask = this.dom_container.parentNode.querySelector( '.chart-container-mask' );

      if ( this.options.elevation.view_3d ) {

        this.instance.on( 'moveend', ( e ) => {
          if ( e.hasOwnProperty( 'extrusion' ) ) {
            this.instance.setLayoutProperty( '_path-elevation-extrusion', 'visibility', e.extrusion );
          }
        } );

      }

      //

      btn.addEventListener( 'click', this._toggle_chart.bind( this ) );
      mask.addEventListener( 'click', this._toggle_chart.bind( this ) );

      window._tooltip.updateSources();

    }

  }

  _toggle_chart() {

    if ( this._get_chart_container().classList.contains( 'show' ) ) {

      this._get_chart_container().classList.remove( 'show' );
      this.dom_container.parentNode.querySelector( '.chart-container-mask' ).classList.remove( 'show' );

      if ( this.options.elevation.view_3d ) {
        this.instance.setLayoutProperty( '_path-elevation-extrusion', 'visibility', 'none' );
        this.instance.easeTo( this.camera_save );
      }

    }
    else {

      this._get_chart_container().classList.add( 'show' );
      this.dom_container.parentNode.querySelector( '.chart-container-mask' ).classList.add( 'show' );

      if ( this.options.elevation.view_3d ) {
        this.camera_save = {
          pitch: this.instance.getPitch(),
          bearing: this.instance.getBearing(),
          zoom: this.instance.getZoom(),
          center: this.instance.getCenter(),
          duration: this.options.elevation.view_3d.duration
        };
        this.instance.easeTo( this.options.elevation.view_3d, { 'extrusion': 'visible' } );
      }

    }

  }

  // https://www.mapbox.com/mapbox-gl-js/example/drag-a-point/
  init_debug() {

    const coordinates = document.createElement( 'pre' );
    coordinates.setAttribute( 'style', 'z-index: 2; background: rgba(0,0,0,0.74); color: #fff; position: absolute; bottom: 40px; left: 10px; padding:5px 10px; margin: 0; font-size: 11px; line-height: 18px; border-radius: 3px; display: none;' );
    document.getElementById( this.options.dom_id ).parentNode.insertBefore( coordinates, document.getElementById( this.options.dom_id ).nextSibling );

    const canvas = this.instance.getCanvasContainer();
    const center = this.instance.unproject( [ 30, 120 ] );

    const geojson = {
      "type": "FeatureCollection",
      "features": [ {
        "type": "Feature",
        "geometry": {
          "type": "Point",
          "coordinates": [ center.lng, center.lat ],
        }
      } ]
    };

    const handlers = {

      onMove: ( e ) => {

        const coords = e.lngLat;

        // Set a UI indicator for dragging.
        canvas.style.cursor = 'grabbing';

        // Update the Point feature in `geojson` coordinates
        // and call setData to the source layer `point` on it.
        geojson.features[ 0 ].geometry.coordinates = [ coords.lng, coords.lat ];
        this.instance.getSource( 'debug-point' ).setData( geojson );

      },

      onUp: ( e ) => {

        const coords = e.lngLat;

        // Print the coordinates of where the point had
        // finished being dragged to on the map.
        coordinates.style.display = 'block';
        coordinates.innerHTML = `Longitude: ${coords.lng}<br />Latitude: ${coords.lat}<br />${coords.lat.toFixed( 5 )}, ${coords.lng.toFixed( 5 )}<br />Zoom: ${this.instance.getZoom()}`;
        canvas.style.cursor = '';

        // Unbind mouse/touch events
        this.instance.off( 'mousemove', handlers.onMove );
        this.instance.off( 'touchmove', handlers.onMove );

      }

    };

    this.instance.on( 'load', () => {

      // Add a single point to the map
      this.instance.addSource( 'debug-point', {
        "type": "geojson",
        "data": geojson
      } );

      this.instance.addLayer( {
        "id": "debug-point",
        "type": "circle",
        "source": "debug-point",
        "paint": {
          "circle-radius": 10,
          "circle-color": "#3887be"
        }
      } );

      // When the cursor enters a feature in the point layer, prepare for dragging.
      this.instance.on( 'mouseenter', 'debug-point', () => {
        this.instance.setPaintProperty( 'debug-point', 'circle-color', '#3bb2d0' );
        canvas.style.cursor = 'move';
      } );

      this.instance.on( 'mouseleave', 'debug-point', () => {
        this.instance.setPaintProperty( 'debug-point', 'circle-color', '#3887be' );
        canvas.style.cursor = '';
      } );

      this.instance.on( 'mousedown', 'debug-point', ( e ) => {
        // Prevent the default map drag behavior.
        e.preventDefault();
        canvas.style.cursor = 'grab';
        this.instance.on( 'mousemove', handlers.onMove );
        this.instance.once( 'mouseup', handlers.onUp );
      } );

      this.instance.on( 'touchstart', 'debug-point', ( e ) => {
        if ( e.points.length !== 1 ) return;
        // Prevent the default map drag behavior.
        e.preventDefault();
        this.instance.on( 'touchmove', handlers.onMove );
        this.instance.once( 'touchend', handlers.onUp );
      } );

      this.instance.on( 'moveend', () => {
        const center = this.instance.unproject( [ 30, 120 ] );
        this.instance.getSource( 'debug-point' ).setData( {
          "type": "FeatureCollection",
          "features": [ {
            "type": "Feature",
            "geometry": {
              "type": "Point",
              "coordinates": [ center.lng, center.lat ],
            }
          } ]
        } );
      } );

    } );

  }

  // decodePolygon( array ) {
  // }

  // encodePolygon( feature ) {

  //   const encoded = [];

  //   if ( 'Polygon' !== feature.geometry.type ) {
  //     throw "Cannot encodePolygon if not a Polygon, you dumb-dumb.";
  //   }

  //   for ( const coordinatesArray of feature.geometry.coordinates ) {
  //     console.log( coordinatesArray );
  //     console.log( turf_lineString( coordinatesArray ) );
  //     encoded.push( polyline.fromGeoJSON( turf_lineString( coordinatesArray ) ) );
  //   }

  //   return encoded;

  // }

}
